import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';
import TimeAgo from './TimeAgo';
import { useUI } from '../../context/UIContext';
import { useAuth } from '../../context/AuthContext';
import { companiesAPI } from '../../services/api';
import { ROUTES } from '../../constants/constants';
import Button from './Button';
import {
  RiClipboardFill, RiCheckboxCircleFill, RiCloseCircleFill, RiErrorWarningFill,
  RiLockUnlockFill, RiLockFill, RiBuilding2Fill, RiUserAddFill, RiAlarmFill, RiNotification3Fill,
} from 'react-icons/ri';
import { TbConfetti, TbArchive } from 'react-icons/tb';
import { Icon } from './Icon';

/* ── Type → presentation maps ─────────────────────────────────────────── */
// Line icons (not emoji) so the notification tiles match the line-glyph
// language used across the sidebar, search, and dashboard.
const TYPE_ICON = {
  assignment_assigned: RiClipboardFill,
  assignment_accepted: RiCheckboxCircleFill,
  assignment_rejected: RiCloseCircleFill,
  assignment_revoked: RiErrorWarningFill,
  access_granted: RiLockUnlockFill,
  access_revoked: RiLockFill,
  company_claimed: RiBuilding2Fill,
  company_access_requested: RiUserAddFill,
  company_access_approved: RiLockUnlockFill,
  company_access_denied: RiCloseCircleFill,
  company_access_reminder: RiAlarmFill,
  system: RiNotification3Fill,
  admin_alert_account_locked: RiLockFill,
  admin_alert_user_compromised: RiErrorWarningFill,
  admin_alert_new_flag: RiErrorWarningFill,
  admin_alert_critical_error: RiErrorWarningFill,
};

const TYPE_COLOR = {
  assignment_assigned: '#145f5f',
  assignment_accepted: '#4a6b3f',
  assignment_rejected: '#9a564a',
  assignment_revoked: '#8a6a1f',
  access_granted: '#4a6b3f',
  access_revoked: '#9a564a',
  company_claimed: '#145f5f',
  company_access_requested: '#836016',
  company_access_approved: '#4a6b3f',
  company_access_denied: '#9a564a',
  company_access_reminder: '#8a6a1f',
  system: '#7a7060',
  admin_alert_account_locked: '#8a6a1f',
  admin_alert_user_compromised: '#9a3a3a',
  admin_alert_new_flag: '#9a564a',
  admin_alert_critical_error: '#9a3a3a',
};

const TYPE_LABEL = {
  assignment_assigned: 'Assignment',
  assignment_accepted: 'Assignment',
  assignment_rejected: 'Assignment',
  assignment_revoked: 'Assignment',
  access_granted: 'Access',
  access_revoked: 'Access',
  company_claimed: 'Company',
  company_access_requested: 'Access',
  company_access_approved: 'Access',
  company_access_denied: 'Access',
  company_access_reminder: 'Reminder',
  system: 'System',
  admin_alert_account_locked: 'Security',
  admin_alert_user_compromised: 'Security',
  admin_alert_new_flag: 'Security',
  admin_alert_critical_error: 'Security',
};

const iconCmpFor = (t) => TYPE_ICON[t] || RiNotification3Fill;
const colorFor = (t) => TYPE_COLOR[t] || '#7a7060';
const labelFor = (t) => TYPE_LABEL[t] || 'Update';

/* ── Time helpers ─────────────────────────────────────────────────────── */
function formatTimeAgo(dateString) {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 45) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  if (diff < 2592000) return `${Math.floor(diff / 604800)}w ago`;
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

// Buckets a notification into a date group for the Notion-style headers.
function dateBucket(dateString) {
  const d = new Date(dateString);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  const startOfWeek = new Date(startOfToday);
  startOfWeek.setDate(startOfWeek.getDate() - 7);
  if (d >= startOfToday) return 'Today';
  if (d >= startOfYesterday) return 'Yesterday';
  if (d >= startOfWeek) return 'Earlier this week';
  return 'Older';
}
const BUCKET_ORDER = ['Today', 'Yesterday', 'Earlier this week', 'Older'];

// Where a notification should take the user when clicked. `null` → no nav.
function routeForNotification(type, roleName) {
  const isPro = roleName === 'professional';
  if (!type) return null;
  // Security alerts (recipient is always a super_admin) deep-link into the
  // admin surface that resolves them.
  if (type === 'admin_alert_new_flag' || type === 'admin_alert_user_compromised') {
    return ROUTES.ADMIN_FLAG_INBOX;
  }
  if (type === 'admin_alert_account_locked') {
    return `${ROUTES.ADMIN_USERS}?filter=locked`;
  }
  if (type === 'admin_alert_critical_error') {
    return ROUTES.ADMIN_ERROR_LOGS;
  }
  if (type.startsWith('assignment_')) {
    return isPro ? ROUTES.PROFESSIONAL_DASHBOARD : ROUTES.MANAGE_COLLABORATORS;
  }
  if (type === 'company_access_requested' || type === 'company_access_reminder') {
    return ROUTES.MANAGE_COLLABORATORS; // recipient is the company owner
  }
  if (
    type === 'company_access_approved' ||
    type === 'company_access_denied' ||
    type === 'access_revoked' ||
    type === 'company_claimed'
  ) {
    // recipient is the requester / grantee
    return isPro ? `${ROUTES.PROFESSIONAL_DASHBOARD}?tab=requests` : ROUTES.MANAGE_COLLABORATORS;
  }
  return null;
}

/* ── Inline action icons ──────────────────────────────────────────────── */
const svgProps = {
  width: 13,
  height: 13,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2.2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};
const IconArchive = () => (
  <svg {...svgProps}>
    <rect x="3" y="4" width="18" height="4.5" rx="1" />
    <path d="M5 8.5V18a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8.5" />
    <path d="M10 13h4" />
  </svg>
);
const IconRestore = () => (
  <svg {...svgProps}>
    <path d="M9 14 4 9l5-5" />
    <path d="M4 9h11a5 5 0 0 1 0 10H9" />
  </svg>
);
const IconTrash = () => (
  <svg {...svgProps}>
    <path d="M4 7h16" />
    <path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    <path d="M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" />
  </svg>
);

function ActionButton({ title, tone = '#7a7060', onClick, children }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className="w-[26px] h-[26px] rounded-[7px] flex items-center justify-center transition-colors"
      style={{ color: tone, background: 'transparent' }}
      onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(8,17,31,0.07)'; }}
      onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      {children}
    </button>
  );
}

/* ── A single notification row ────────────────────────────────────────── */
function NotificationItem({
  n,
  view,
  onOpen,
  onArchive,
  onUnarchive,
  onDelete,
  accessActioning,
  accessResolved,
  onAccess,
}) {
  const color = colorFor(n.type);
  const isArchivedView = view === 'archived';
  const showAccessActions = (n.type === 'company_access_requested' || n.type === 'company_access_approved') && !isArchivedView;
  const senderName = n.sender?.fullName || n.sender?.name;
  const isUnread = !n.read && !isArchivedView;
  // Unread rows get a faint teal wash so the bell badge count is traceable
  // to specific items; hover still wins while the pointer is over the row.
  const baseBg = isUnread ? 'rgba(20,95,95,0.05)' : 'transparent';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(n)}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(n); }}
      className="group relative flex gap-3 px-4 py-3 cursor-pointer transition-colors outline-none"
      style={{ background: baseBg }}
      onMouseOver={(e) => { e.currentTarget.style.background = '#f7f4ec'; }}
      onMouseOut={(e) => {
        e.currentTarget.style.background = baseBg;
      }}
    >

      {/* Type icon — toned tile with a line glyph, matching the app's icon language */}
      {(() => {
        const TypeIcon = iconCmpFor(n.type);
        return (
          <div
            className="shrink-0 w-9 h-9 rounded-[10px] flex items-center justify-center border transition-transform duration-200 group-hover:scale-105"
            style={{ background: `${color}14`, borderColor: `${color}2e`, color }}
          >
            <TypeIcon size={17} />
          </div>
        );
      })()}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span
            className="text-[9.5px] font-bold uppercase tracking-[0.4px] px-1.5 py-[1px] rounded"
            style={{ background: `${color}1a`, color }}
          >
            {labelFor(n.type)}
          </span>
          <span className="ml-auto text-[10.5px] shrink-0" style={{ color: '#a39a88' }}>
            <TimeAgo iso={n.createdAt}>{formatTimeAgo(n.createdAt)}</TimeAgo>
          </span>
          {isUnread && (
            <span
              className="w-[7px] h-[7px] rounded-full shrink-0"
              title="Unread"
              style={{ background: '#145f5f' }}
            />
          )}
        </div>

        <div
          className="text-[12.5px] font-semibold mt-1 leading-snug"
          style={{ color: '#08111f' }}
        >
          {n.title}
        </div>
        <div
          className="text-[11.5px] mt-0.5 leading-relaxed line-clamp-2"
          style={{ color: '#7a7060' }}
        >
          {n.message}
        </div>
        {senderName && (
          <div className="text-[10.5px] mt-1" style={{ color: '#a39a88' }}>
            from {senderName}
          </div>
        )}

        {/* Inline approve / decline for access requests. Archive/Delete live
            only in the hover cluster (top-right) to keep rows uncluttered. */}
        {showAccessActions && (
          n.type === 'company_access_approved' ? (
            <div className="flex items-center justify-between mt-2">
              <div
                className="text-[11px] font-semibold"
                style={{ color: '#4a6b3f' }}
              >
                ✓ Access granted
              </div>
            </div>
          ) : (
            // company_access_requested
            accessResolved ? (
              <div className="flex items-center justify-between mt-2">
                <div
                  className="text-[11px] font-semibold"
                  style={{ color: accessResolved === 'approved' ? '#4a6b3f' : '#9a564a' }}
                >
                  {accessResolved === 'approved' ? '✓ Access granted' : '✕ Request declined'}
                </div>
              </div>
            ) : (
              <div className="flex gap-2 mt-2">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={accessActioning}
                  onClick={(e) => onAccess(e, n, 'approve')}
                >
                  Approve
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={accessActioning}
                  onClick={(e) => onAccess(e, n, 'deny')}
                >
                  Decline
                </Button>
              </div>
            )
          )
        )}
      </div>

      {/* Hover actions — masks the timestamp with the row's hover colour */}
      <div
        className="absolute top-[8px] right-[8px] flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ background: '#f7f4ec' }}
        onClick={(e) => e.stopPropagation()}
      >
        {isArchivedView ? (
          <>
            <ActionButton title="Restore to inbox" tone="#145f5f" onClick={() => onUnarchive(n._id)}>
              <IconRestore />
            </ActionButton>
            <ActionButton title="Delete permanently" tone="#9a564a" onClick={() => onDelete(n._id)}>
              <IconTrash />
            </ActionButton>
          </>
        ) : (
          <>
            <ActionButton title="Archive" tone="#7a7060" onClick={() => onArchive(n._id)}>
              <IconArchive />
            </ActionButton>
            <ActionButton title="Delete permanently" tone="#9a564a" onClick={() => onDelete(n._id)}>
              <IconTrash />
            </ActionButton>
          </>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════ */

const TABS = [
  { id: 'inbox', label: 'Inbox' },
  { id: 'archived', label: 'Archived' },
];

const NotificationDropdown = () => {
  const {
    notifications,
    archived,
    archivedLoaded,
    counts,
    loading,
    showNotifications,
    setShowNotifications,
    archiveNotification,
    unarchiveNotification,
    deleteNotification,
    fetchNotifications,
    fetchArchived,
    isConnected,
    markAsRead,
    markAllAsRead,
  } = useNotifications();

  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { showToast } = useUI();
  const { user } = useAuth();
  const roleName = String(user?.roleName || '').toLowerCase();

  const [view, setView] = useState('inbox');

  // Inline Approve/Decline for company access requests. The notification
  // carries the company id + requester (sender) but not the grant id, so the
  // pending grant is resolved by listing the company's grants first.
  const [actioningId, setActioningId] = useState(null);
  const [resolved, setResolved] = useState({}); // notificationId -> 'approved' | 'declined'

  // Lazy-load the archive the first time its tab is opened.
  useEffect(() => {
    if (view === 'archived' && !archivedLoaded) fetchArchived();
  }, [view, archivedLoaded, fetchArchived]);

  // Refresh the inbox whenever the panel is opened — a safety net for any
  // notifications missed while the WebSocket was disconnected.
  useEffect(() => {
    if (showNotifications) fetchNotifications();
  }, [showNotifications, fetchNotifications]);

  // Legacy fallback only. Requests resolved since the `actionState` stamp
  // shipped carry their outcome on the notification itself and render final
  // on first paint. Older rows (stamped before that, or resolved from
  // Manage Collaborators on an old build) still need their state re-derived
  // from the grant — one /access lookup per company, only when such rows
  // exist. Delete this effect once pre-stamp notifications have aged out
  // (30-day inbox TTL).
  useEffect(() => {
    if (!showNotifications) return undefined;
    const requestNotifs = notifications.filter(
      (n) =>
        n.type === 'company_access_requested' &&
        !n.actionState &&
        !n.archived &&
        n.relatedEntity?.id
    );
    if (!requestNotifs.length) return undefined;

    let cancelled = false;
    const companyIds = [
      ...new Set(requestNotifs.map((n) => String(n.relatedEntity.id))),
    ];
    Promise.all(
      companyIds.map((cid) =>
        companiesAPI.access
          .list(cid)
          .then((res) => [cid, res?.data || []])
          .catch(() => [cid, null]) // lookup failed → leave it actionable
      )
    ).then((pairs) => {
      if (cancelled) return;
      const grantsByCompany = Object.fromEntries(pairs);
      const next = {};
      for (const n of requestNotifs) {
        const grants = grantsByCompany[String(n.relatedEntity.id)];
        if (!grants) continue;
        const requesterId = String(n.sender?._id || n.sender || '');
        const grant = grants.find(
          (g) => String(g.user?._id || g.user) === requesterId
        );
        // 'pending' → still actionable; anything else → already handled.
        if (grant && grant.status !== 'pending') {
          next[n._id] = grant.grantedAt ? 'approved' : 'declined';
        }
      }
      if (Object.keys(next).length) {
        setResolved((r) => ({ ...r, ...next }));
        // A request that's already been handled shouldn't keep the bell lit —
        // clear unread for the rows we just resolved (heals notifications
        // approved before acting-marks-read existed, or from another device).
        for (const id of Object.keys(next)) {
          const notif = requestNotifs.find((n) => n._id === id);
          if (notif && !notif.read) markAsRead(id);
        }
      }
    });

    return () => {
      cancelled = true;
    };
  }, [showNotifications, notifications, markAsRead]);

  // Close on outside click.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setShowNotifications]);

  const handleAccessAction = async (e, notification, decision) => {
    e.stopPropagation();
    const companyId = notification.relatedEntity?.id;
    const requesterId = notification.sender?._id;
    if (!companyId || !requesterId) {
      showToast('This request is missing details — open Manage Collaborators.', 'error');
      return;
    }
    setActioningId(notification._id);
    try {
      const listRes = await companiesAPI.access.list(companyId);
      const grant = (listRes?.data || []).find(
        (g) =>
          g.status === 'pending' &&
          String(g.user?._id || g.user) === String(requesterId)
      );
      if (!grant) {
        setResolved((r) => ({ ...r, [notification._id]: decision === 'approve' ? 'approved' : 'declined' }));
        // Acting on a request is the strongest form of reading it — without
        // this, the bell badge stays lit even after the request is resolved.
        markAsRead(notification._id);
        showToast('This request was already handled.', 'success');
        return;
      }
      const res =
        decision === 'approve'
          ? await companiesAPI.access.approve(companyId, grant._id)
          : await companiesAPI.access.deny(companyId, grant._id);
      if (res?.success) {
        setResolved((r) => ({ ...r, [notification._id]: decision === 'approve' ? 'approved' : 'declined' }));
        markAsRead(notification._id);
        showToast(decision === 'approve' ? 'Access granted' : 'Access request declined', 'success');
      } else {
        showToast(res?.message || 'Could not complete the action', 'error');
      }
    } catch (err) {
      showToast(err?.message || 'Could not complete the action', 'error');
    } finally {
      setActioningId(null);
    }
  };

  const handleOpen = (n) => {
    // Opening a notification is reading it — clear the unread state so the
    // bell dot reflects reality without needing a separate action.
    if (!n.read && !n.archived) markAsRead(n._id);
    const route = routeForNotification(n.type, roleName);
    setShowNotifications(false);
    if (route) navigate(route);
  };

  // The list shown for the active tab.
  const displayed = useMemo(() => {
    if (view === 'archived') return archived;
    return notifications;
  }, [view, notifications, archived]);

  // Date-grouped, preserving the server's newest-first order.
  const groups = useMemo(() => {
    const map = {};
    for (const n of displayed) {
      const b = dateBucket(n.createdAt);
      (map[b] = map[b] || []).push(n);
    }
    return BUCKET_ORDER.filter((b) => map[b]).map((b) => ({ bucket: b, items: map[b] }));
  }, [displayed]);

  
  const tabCount = (id) => {
    if (id === 'inbox') return counts.inbox;
    return counts.archived;
  };

  const emptyState = () => {
    const map = {
      inbox: { icon: TbConfetti, color: '#4a6b3f', title: "You're all caught up", sub: 'New notifications will show up here.' },
      archived: { icon: TbArchive, color: '#a39a88', title: 'Nothing archived yet', sub: 'Archived notifications are kept here for 90 days.' },
    };
    const e = map[view];
    return (
      <div className="px-6 py-12 text-center">
        <div className="flex justify-center mb-2"><Icon icon={e.icon} size={30} style={{ color: e.color }} /></div>
        <div className="text-[13px] font-semibold" style={{ color: '#08111f' }}>{e.title}</div>
        <div className="text-[11.5px] mt-1" style={{ color: '#a39a88' }}>{e.sub}</div>
      </div>
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Notification Bell — cohesive with the search trigger + Add Company,
          with a hover wiggle, active press, and an unread badge. */}
      <button
        className="group relative shrink-0 grid place-items-center rounded-xl border-[1.5px] transition-all duration-200 active:scale-95"
        style={{
          width: 34,
          height: 34,
          borderColor: '#ddd6c6',
          background: showNotifications ? '#faf7f2' : '#ffffff',
        }}
        onClick={() => setShowNotifications(!showNotifications)}
        title={`Notifications${isConnected ? '' : ' · offline'}`}
        onMouseOver={(e) => { e.currentTarget.style.background = '#faf7f2'; }}
        onMouseOut={(e) => { e.currentTarget.style.background = showNotifications ? '#faf7f2' : '#ffffff'; }}
      >
        <svg
          width="17" height="17" viewBox="0 0 24 24" fill="none"
          stroke="#5a5145"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          className={`transition-transform duration-200 origin-top group-hover:scale-110 group-hover:-rotate-[10deg] ${showNotifications ? 'scale-110 -rotate-[10deg]' : ''}`}
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>

        {/* Badge counts UNREAD, not inbox size — reading clears it; archiving
            is not required just to silence the bell. */}
        {counts.unread > 0 && (
          <span
            className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 grid place-items-center rounded-full text-[9px] font-bold text-white leading-none"
            style={{ background: '#9a564a', boxShadow: '0 0 0 2px #fff' }}
          >
            {counts.unread > 99 ? '99+' : counts.unread}
          </span>
        )}
      </button>

      {/* Notification Panel */}
      {showNotifications && (
        <div
          className="absolute right-0 mt-2 w-[416px] rounded-[14px] overflow-hidden"
          style={{
            background: '#ffffff',
            border: '1px solid #ddd6c6',
            boxShadow: '0 16px 48px rgba(8,17,31,0.18)',
            zIndex: 1000,
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 pt-3.5 pb-3 border-b"
            style={{ borderColor: '#eee6d8' }}
          >
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-[15px]" style={{ color: '#08111f' }}>
                Notifications
              </h3>
            </div>
            <span
              className="flex items-center gap-1.5 text-[10.5px] font-medium"
              style={{ color: isConnected ? '#4a6b3f' : '#a39a88' }}
            >
              <span
                className="w-[6px] h-[6px] rounded-full"
                style={{ background: isConnected ? '#4a6b3f' : '#c2bba8' }}
              />
              {isConnected ? 'Live' : 'Offline'}
            </span>
          </div>

          {/* Segmented tabs */}
          <div className="px-3 pt-2.5 pb-1.5">
            <div
              className="flex gap-1 p-[3px] rounded-[10px]"
              style={{ background: '#f0ece2' }}
            >
              {TABS.map((t) => {
                const active = view === t.id;
                const c = tabCount(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setView(t.id)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-[6px] rounded-[8px] text-[11.5px] font-semibold transition-all"
                    style={{
                      background: active ? '#ffffff' : 'transparent',
                      color: active ? '#145f5f' : '#7a7060',
                      boxShadow: active ? '0 1px 4px rgba(8,17,31,0.10)' : 'none',
                    }}
                  >
                    {t.label}
                    {c > 0 && (
                      <span
                        className="text-[9.5px] font-bold px-1 py-[1px] rounded-full min-w-[15px]"
                        style={{
                          background: active ? '#145f5f' : '#ddd6c6',
                          color: active ? '#ffffff' : '#7a7060',
                        }}
                      >
                        {c > 99 ? '99+' : c}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Bulk actions */}
          {view === 'inbox' && notifications.length > 0 && (
            <div
              className="flex items-center gap-3 px-4 py-1.5 text-[11px] font-semibold border-b"
              style={{ borderColor: '#f3eee2' }}
            >
              {counts.unread > 0 && (
                <button
                  type="button"
                  onClick={markAllAsRead}
                  className="transition-opacity hover:opacity-70"
                  style={{ color: '#145f5f' }}
                >
                  Mark all read
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  notifications.forEach(n => archiveNotification(n._id));
                }}
                className="transition-opacity hover:opacity-70"
                style={{ color: '#7a7060' }}
              >
                Archive all
              </button>
            </div>
          )}
          {view === 'archived' && archived.length > 0 && (
            <div
              className="flex items-center gap-3 px-4 py-1.5 text-[11px] font-semibold border-b"
              style={{ borderColor: '#f3eee2' }}
            >
              <button
                type="button"
                onClick={() => {
                  archived.forEach(n => deleteNotification(n._id));
                }}
                className="transition-opacity hover:opacity-70"
                style={{ color: '#9a564a' }}
              >
                Delete all archived
              </button>
            </div>
          )}

          {/* List */}
          <div className="overflow-y-auto" style={{ maxHeight: '420px' }}>
            {(loading && displayed.length === 0) || (view === 'archived' && !archivedLoaded) ? (
              <div className="px-6 py-12 text-center">
                <div
                  className="w-5 h-5 mx-auto border-2 rounded-full animate-spin"
                  style={{ borderColor: '#ddd6c6', borderTopColor: '#145f5f' }}
                />
                <div className="text-[11.5px] mt-3" style={{ color: '#a39a88' }}>
                  Loading notifications…
                </div>
              </div>
            ) : displayed.length === 0 ? (
              emptyState()
            ) : (
              groups.map((g) => (
                <div key={g.bucket}>
                  <div
                    className="px-4 pt-3 pb-1 text-[9.5px] font-bold uppercase tracking-[0.7px] sticky top-0"
                    style={{ color: '#a39a88', background: '#ffffff', zIndex: 1 }}
                  >
                    {g.bucket}
                  </div>
                  {g.items.map((n) => (
                    <NotificationItem
                      key={n._id}
                      n={n}
                      view={view}
                      onOpen={handleOpen}
                      onArchive={archiveNotification}
                      onUnarchive={unarchiveNotification}
                      onDelete={deleteNotification}
                      accessActioning={actioningId === n._id}
                      accessResolved={n.actionState || resolved[n._id]}
                      onAccess={handleAccessAction}
                    />
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
